import { StarsButton as Button, Translation, usePrevious } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { interval } from 'rxjs';

import Reload from '../../assets/reload.svg';

import {
  TXTWCL_Last_Updated_N0_minutes_ago,
  TXTWCL_Last_Updated_N0_seconds_ago,
  TXTWCL_Last_Updated_just_now
} from './RefreshButton.messages.json';
import styles from './RefreshButton.scss';

interface Props {
  refreshTimestamp: number;
  onClick(): void;
  className?: string;
}

export const createTimestamp = (time = Date.now()) => time / 1000;
const difference = (time: number): number => Math.floor(createTimestamp() - time);

const RefreshButton: React.FC<Props> = ({ refreshTimestamp, onClick, className }) => {
  const [timeDifference, setTimeDifference] = useState(difference(refreshTimestamp));
  const { ref, inView } = useInView({ threshold: 0 });

  const [message, setMessage] = useState(<Translation {...TXTWCL_Last_Updated_just_now} />);
  const oldRefreshTimestamp = usePrevious(refreshTimestamp);

  const updateDifference = useCallback(() => {
    setTimeDifference(difference(refreshTimestamp));
  }, [refreshTimestamp]);

  useEffect(() => {
    const differenceUpdate = (timeDifference < 30 ? 30 : 60) - timeDifference;
    if (differenceUpdate > 0) {
      const baseInterval = interval(differenceUpdate * 1000).subscribe(() => updateDifference());

      return () => {
        baseInterval.unsubscribe();
      };
    }

    if (inView && timeDifference >= 60 && oldRefreshTimestamp === refreshTimestamp) {
      onClick();
    }

    return undefined;
  }, [refreshTimestamp, oldRefreshTimestamp, timeDifference, updateDifference, inView, onClick]);

  useEffect(() => {
    inView && updateDifference();
  }, [inView, updateDifference]);

  useEffect(() => {
    if (timeDifference < 30) {
      setMessage(<Translation {...TXTWCL_Last_Updated_just_now} />);
    } else if (timeDifference >= 30 && timeDifference < 60) {
      setMessage(
        <Translation {...TXTWCL_Last_Updated_N0_seconds_ago} values={{ 0: timeDifference }} />
      );
    } else if (timeDifference >= 60) {
      setMessage(
        <Translation
          {...TXTWCL_Last_Updated_N0_minutes_ago}
          values={{ 0: Math.floor(timeDifference / 60) }}
        />
      );
    } else {
      setMessage(<Translation {...TXTWCL_Last_Updated_just_now} />);
    }
  }, [timeDifference]);

  return (
    <div ref={ref} className={classNames(styles.wrap, className)}>
      <Button onClick={onClick} variant="casino" className={styles.reload} icon={<Reload />} />
      <div className={styles.text}>{message}</div>
    </div>
  );
};

export default RefreshButton;
